import { SEOHead } from "@/components/seo/SEOHead";
import { useRoOnlyGuard } from '@/hooks/useRoOnlyGuard';

const LegalLayout = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="container mx-auto px-4 py-10 max-w-4xl">
    <div className="surface-dark band-bleed -mt-10 mb-8 py-6"><h1 className="text-3xl md:text-4xl font-bold">{title}</h1></div>
    <div className="prose prose-neutral dark:prose-invert max-w-none space-y-6">{children}</div>
  </div>
);

export default function PoliticaConfidentialitate() {
  const __roBlocked = useRoOnlyGuard();
  return (
    <>
      <SEOHead
        title="Politica de Confidențialitate - ROAutoMarket"
        description="Cum colectăm, folosim și protejăm datele tale personale conform GDPR și legislației române."
        canonicalPath="/politica-de-confidentialitate"
      />
      <LegalLayout title="Politica de Confidențialitate">
        <p className="text-sm text-muted-foreground">Ultima actualizare: 25.09.2026</p>

        <section>
          <h2 className="text-2xl font-semibold mb-3">1. Cine suntem</h2>
          <p>
            Operatorul datelor este <strong>Vladimir Tudor</strong>, comerciant individual (Sole Trader) înregistrat în
            Regatul Unit (UTR HMRC: 9810367572), cu adresa 62 Clarkson Court, Hatfield, AL10 9GW, United Kingdom,
            care administrează platforma <strong>roautomarket.ro</strong>. Pentru că oferim servicii utilizatorilor din
            România și UE, prelucrarea datelor respectă atât <strong>UK GDPR</strong>, cât și <strong>Regulamentul (UE) 2016/679 (GDPR)</strong>.
            Datele complete de contact sunt disponibile pe pagina{" "}
            <a href="/mentiuni-legale" className="text-primary hover:underline">Mențiuni Legale</a>.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-3">2. Ce date colectăm</h2>
          <p>Prelucrăm următoarele categorii de date pentru funcționarea platformei:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Email</strong> – pentru crearea contului, autentificare și comunicări tranzacționale (notificări lead, expirare anunțuri, facturi).</li>
            <li><strong>Nume complet</strong> – introdus în formularul de cont.</li>
            <li><strong>Datele dealerului</strong> – nume, prenume, numele firmei, CUI, forma de organizare, adresă sau locație și descrierea profilului, pentru administrarea contului și verificarea firmei.</li>
            <li><strong>Număr de telefon</strong> – afișat în anunțurile dealerilor pentru a fi contactați de cumpărători și folosit opțional pentru notificări WhatsApp.</li>
            <li><strong>Parolă</strong> – stocată exclusiv criptată (hash), nu o vedem niciodată în clar.</li>
            <li><strong>Conținutul anunțurilor publicate de dealeri</strong> – fotografii vehicul, descriere, preț, locație (oraș).</li>
            <li><strong>Date tehnice</strong> – adresă IP și tip browser, inclusiv pentru securitate și prevenirea fraudei.</li>
            <li><strong>Date de analiză</strong> (PostHog, Sentry) – pagini vizitate, evenimente UX – <strong>numai dacă accepți cookies de analiză</strong> din banner. Pseudonimizate.</li>
            <li><strong>Date de plată</strong> – procesate exclusiv de <strong>Stripe</strong>. Noi <strong>nu</strong> stocăm numere de card, CVV sau IBAN.</li>
          </ul>
          <p>Nu solicităm introducerea numerelor de card sau a codurilor CVV în formularele platformei. Plățile sunt procesate de Stripe.</p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-3">3. De ce le colectăm (scopuri și temei legal)</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Funcționarea contului și a platformei</strong> (autentificare, publicare anunțuri, mesagerie) – executare contract (art. 6(1)(b) GDPR).</li>
            <li><strong>Securitate și prevenire fraudă</strong> (loguri IP, rate limiting) – interes legitim (art. 6(1)(f) GDPR).</li>
            <li><strong>Analiză și îmbunătățire platformă</strong> – consimțământ (art. 6(1)(a) GDPR).</li>
            <li><strong>Marketing și newsletter</strong> – consimțământ explicit (art. 6(1)(a) GDPR + Legea 506/2004 art. 12).</li>
            <li><strong>Obligații contabile pentru facturile Stripe</strong> – obligație legală (art. 6(1)(c) GDPR + legislație fiscală UK).</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-3">4. Cât timp păstrăm datele</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Cont și anunțuri:</strong> pe durata existenței contului. La cererea de ștergere, datele sunt eliminate în maximum 30 de zile.</li>
            <li><strong>Loguri tehnice (IP):</strong> maximum 12 luni.</li>
            <li><strong>Documente contabile și facturi Stripe:</strong> 6 ani (cerință HMRC, Regatul Unit).</li>
            <li><strong>Date de marketing:</strong> până când îți retragi consimțământul (prin link de dezabonare din orice email).</li>
          </ul>
          <p className="mt-3">Accesul administrativ la datele contului este înregistrat într-un jurnal de audit.</p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-3">5. Cu cine partajăm datele</h2>
          <p>Folosim furnizori terți care acționează ca persoane împuternicite:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Supabase</strong> – găzduire bază de date și autentificare.</li>
            <li><strong>Lovable</strong> – găzduirea aplicației.</li>
            <li><strong>Stripe</strong> – procesare plăți.</li>
            <li><strong>Resend</strong> – trimitere emailuri tranzacționale.</li>
            <li><strong>PostHog, Sentry</strong> – analiză și monitorizare erori (doar cu consimțământ).</li>
            <li><strong>Google Gemini prin Lovable AI Gateway</strong> – pentru generarea schiței anunțului din video sunt trimise cadre și datele necesare analizei. Pentru căutarea AI este trimis textul introdus de utilizator.</li>
            <li><strong>Meta (Facebook)</strong> – pixel publicitar (doar cu consimțământ marketing).</li>
            <li><strong>UserWay</strong> – afișarea instrumentului de accesibilitate.</li>
            <li><strong>Google Maps</strong> – afișarea hărții de pe pagina de contact. La încărcarea hărții, Google poate primi date tehnice precum adresa IP.</li>
            <li>Autorități publice, la cererea legală a acestora.</li>
          </ul>
          <p className="mt-4">
            <strong>Acces administrativ pentru suport și operare:</strong> personalul autorizat al operatorului poate accesa, în mod <em>read-only</em> și pentru perioade limitate, datele asociate contului tău de dealer
            (profil, anunțuri și date necesare soluționării sesizărilor) pentru suport tehnic, prevenirea fraudei și moderarea anunțurilor
            (interes legitim, art. 6(1)(f) GDPR). Accesul este limitat la personalul autorizat.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-3">Contactul cu vânzătorul</h2>
          <p>Când contactezi un vânzător, numele, telefonul și mesajul tău ajung la acesta. Vânzătorul le folosește pentru a răspunde solicitării tale.</p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-3">Transferuri în afara UE/SEE</h2>
          <p>Operatorul este înregistrat în Regatul Unit. Furnizori precum Stripe, Lovable, Google, Meta, UserWay, Sentry și PostHog pot prelucra date în afara UE/SEE, în funcție de serviciu și configurare. Google Gemini este accesat prin Lovable AI Gateway. Pentru detalii despre destinații și garanțiile aplicabile, ne poți scrie la contact@roautomarket.ro.</p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-3">6. Drepturile tale</h2>
          <p>Conform GDPR, ai dreptul la:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Acces la datele tale;</li>
            <li>Rectificare;</li>
            <li>Ștergere („dreptul de a fi uitat");</li>
            <li>Restricționarea prelucrării;</li>
            <li>Portabilitate;</li>
            <li>Opoziție la prelucrare;</li>
            <li>Retragerea consimțământului în orice moment.</li>
          </ul>
          <p>Pentru exercitarea acestor drepturi, scrie la <a href="mailto:contact@roautomarket.ro" className="text-primary hover:underline">contact@roautomarket.ro</a>.</p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-3">7. Plângeri</h2>
          <p>Dacă apreciezi că drepturile tale au fost încălcate, te poți adresa autorității competente:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>UK (autoritatea principală a operatorului):</strong> Information Commissioner's Office (ICO),
              Wycliffe House, Water Lane, Wilmslow, Cheshire SK9 5AF –{" "}
              <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">ico.org.uk</a>.
            </li>
            <li>
              <strong>România (pentru utilizatorii din RO):</strong> ANSPDCP, B-dul G-ral. Gheorghe Magheru 28-30,
              Sector 1, București, anspdcp@dataprotection.ro –{" "}
              <a href="https://www.dataprotection.ro" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">dataprotection.ro</a>.
            </li>
            <li>
              <strong>Alte state UE:</strong> autoritatea de supraveghere din statul tău de reședință.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-3">8. Contact responsabil date</h2>
          <p>
            Email: <a href="mailto:contact@roautomarket.ro" className="text-primary hover:underline">contact@roautomarket.ro</a><br />
            Telefon UK: <a href="https://wa.me/447585318863" className="text-primary hover:underline">+44 7585 318863</a> ·
            Telefon RO: <a href="https://wa.me/40732963134" className="text-primary hover:underline">+40 732 963 134</a>
          </p>
        </section>
      </LegalLayout>
    </>
  );
}
